import { usePage } from '@inertiajs/vue3'

/**
 * Reads the editable copy blocks handed down from the server (Inertia `copy` prop, a
 * slug -> text map seeded by ExperimentCopySeeder and editable in ManageExperimentConfig).
 * Returns a getter that resolves a slug to its text, or an empty string when absent.
 */
export function useCopy(): (slug: string) => string {
    const page = usePage()
    return (slug: string): string => {
        const map = page.props.copy as Record<string, string> | undefined
        return map?.[slug] ?? ''
    }
}

/**
 * Returns a copy block as HTML for use with `v-html`. The copy is authored by the researcher
 * in the Filament RichEditor (ManageExperimentConfig), which stores sanitized HTML, so it is
 * rendered as-is. Consumers should wrap the target element in the shared `copy-html` class so
 * the block-level markup (headings, lists, tables, …) is styled — Tailwind's preflight strips
 * element defaults.
 *
 * `vars` substitutes Filament merge tags at display time: the editor serializes a tag as
 * `<span data-type="mergeTag" data-id="ID">…</span>` (and, for hand-typed tokens, the bare
 * `{{ ID }}` form). Both are replaced by `vars[ID]`; an unknown tag is left untouched. Callers
 * that don't pass `vars` get the raw HTML, unchanged.
 */
export function renderCopy(text: string, vars?: Record<string, string>): string {
    if (!vars) {
        return text
    }

    return text
        .replace(
            /<span[^>]*\bdata-type="mergeTag"[^>]*\bdata-id="([^"]+)"[^>]*>.*?<\/span>/g,
            (match, id: string) => vars[id] ?? match,
        )
        .replace(
            /\{\{\s*([\w-]+)\s*\}\}/g,
            (match, id: string) => vars[id] ?? match,
        )
}

/**
 * Whether an onboarding block is shown for the session's treatment. Each block declares an
 * `audience`: 'both' (default), 'help' (only when an assistant is present) or 'no_help' (only
 * without one). The same predicate feeds the gated flow and the "Istruzioni" recap so they never
 * diverge. An unset/unknown audience falls back to 'both' (always shown).
 */
export function blockVisible(block: { audience?: string }, hasChat: boolean): boolean {
    const audience = block.audience ?? 'both'
    return audience === 'both'
        || (audience === 'help' && hasChat)
        || (audience === 'no_help' && !hasChat)
}

/**
 * The substitution map for the `consulente` merge tag, resolved from the session's consultant
 * treatment: 'llm' → "chatbot di intelligenza artificiale", otherwise (human, or as a fallback
 * for a stray tag in a non-hidden block) → "assistente umano". Kept here as the single source
 * so every onboarding surface (gated flow + recap) renders the term identically.
 */
export function consultantTermVars(chatType?: string | null): Record<string, string> {
    return {
        consulente: chatType === 'llm' ? 'chatbot di intelligenza artificiale' : 'assistente umano',
    }
}
